/**
 * @file Contributor UI — backgroundPayloadBuilder.
 * Background Data Payload Builder
 * Builds the payload for saving background data
 */

import {
  dataReviewerEmailKey,
  dataReviewerNameKey,
} from "./backgroundDataReviewers";
import {
  BACKGROUND_GEO_SELECTION_FIELD,
  BACKGROUND_GEO_SPEC_FIELD,
  buildBackgroundGeoApiFields,
} from "../../../../../../utils/backgroundGeographicalSpecificity";
import {
  normalizeDataTypeValue,
  type DataTypeDropdownOption,
} from "../../../../../../components/submission/backgroundDataType";

/** Compute data completeness text from A1/A2/A3/A1-A3 checkboxes */
export function computeDataCompleteness(values: Record<string, string | File>): string {
  const a1 = (values["a1"] as string) === "true";
  const a2 = (values["a2"] as string) === "true";
  const a3 = (values["a3"] as string) === "true";
  const a1_a3 = (values["a1_a3"] as string) === "true";

  if (a1 && a2 && a3) return "A1, A2, and A3 lifecycle stage data reported";
  if (a1_a3 && !a1 && !a2 && !a3) return "Aggregated A1-A3 data reported";
  const count = [a1, a2, a3].filter(Boolean).length;
  if (count === 2) return "Any two lifecycle stage data reported";
  if (count === 1) return "Any one lifecycle stage data reported";
  return "";
}

/** Type definition — Background Payload. */
export interface BackgroundPayload {
  materialId?: number;
  materialCode: string;
  subMaterialId?: number;
  subMaterialUnit?: number;
  finishedQuantity: string;
  materialPropertiesAvailable: string;
  density: string;
  densityUnitId?: number;
  uValue: string;
  uValueUnitId?: number;
  compressiveStrength: string;
  compressiveStrengthUnit?: number;
  tensileStrength: string;
  tensileStrengthUnit?: number;
  temperature: string;
  temperatureUnitId?: number;
  diameter: string;
  diameterUnitId?: number;
  coverage: string;
  coverageUnitId?: number;
  thickness: string;
  thicknessUnitId?: number;
  recycledContent: string;
  materialImageUrl: string;
  publicationFileUrl: string;
  dataType: string;
  dataReview: string;
  dataAcquisition: string;
  dataCompleteness: string;
  geographicalCoverage: string;
  geoRegion: string;
  geographicScope: string;
  country: string;
  productionPlantState: string;
  a1: boolean;
  a2: boolean;
  a3: boolean;
  a1_a3: boolean;
  timelineAvailable: string;
  collectionStartYear: string;
  collectionStartMonth: string;
  collectionStartDay: string;
  collectionEndYear: string;
  collectionEndMonth: string;
  collectionEndDay: string;
  technologyId?: number;
  manufacturerId?: number;
  rawMaterials: Array<{
    rawMaterialName: string;
    qty: string;
    unitId?: number;
  }>;
  dataReviewers: Array<{
    name: string;
    email: string;
  }>;
}

/** Resolve stored URL from API field (`*Url`) or CMS file field name (`materialImage` / `publicationFile`). */
function pickBackgroundFileUrl(
  values: Record<string, string | File>,
  urlKey: string,
  altKey: string
): string {
  const direct = values[urlKey];
  if (typeof direct === "string" && direct.trim()) return direct.trim();
  const alt = values[altKey];
  if (typeof alt === "string" && alt.trim()) return alt.trim();
  return "";
}

/** Exported function — build Background Payload. */
export function buildBackgroundPayload(
  values: Record<string, string | File>,
  rawMaterialGroups: number[],
  reviewerGroups: number[] = [],
  dataTypeOptions: DataTypeDropdownOption[] = []
): BackgroundPayload {
  const rawMaterialsPayload = rawMaterialGroups
    .map((i) => {
      const key = `rawMaterial_${String(i).padStart(2, "0")}`;
      const qtyKey = `${key}_qty`;
      const unitKey = `rawMaterialUnit_${String(i).padStart(2, "0")}`;

      const name = (values[key] as string) || "";
      const qty = (values[qtyKey] as string) || "";
      const unitId = (values[unitKey] as string) || "";
      if (!name && !qty && !unitId) return null; // skip empty groups

      return {
        rawMaterialName: name,
        qty: qty,
        unitId: unitId ? Number(unitId) : undefined,
      };
    })
    .filter(Boolean) as Array<{
    rawMaterialName: string;
    qty: string;
    unitId?: number;
  }>;

  const dataReviewersPayload = reviewerGroups
    .map((i) => ({
      name: String(values[dataReviewerNameKey(i)] ?? "").trim(),
      email: String(values[dataReviewerEmailKey(i)] ?? "").trim(),
    }))
    .filter((row) => row.name || row.email);

  const geoFields = buildBackgroundGeoApiFields(
    values[BACKGROUND_GEO_SPEC_FIELD],
    values[BACKGROUND_GEO_SELECTION_FIELD]
  );

  return {
    materialId: values["materialId"]
      ? Number(values["materialId"])
      : undefined,
    materialCode: (values["materialCode"] as string) || "",
    subMaterialId: values["subMaterialId"]
      ? Number(values["subMaterialId"])
      : undefined,
    subMaterialUnit: values["subMaterialUnit"]
      ? Number(values["subMaterialUnit"])
      : undefined,
    finishedQuantity: (values["finishedQuantity"] as string) || "",
    materialPropertiesAvailable:
      (values["materialPropertiesAvailable"] as string) || "",
    density: (values["density"] as string) || "",
    densityUnitId: values["density_unit"]
      ? Number(values["density_unit"])
      : undefined,
    uValue: (values["uValue"] as string) || "",
    uValueUnitId: values["uValue_unit"]
      ? Number(values["uValue_unit"])
      : undefined,
    compressiveStrength: (values["compressiveStrength"] as string) || "",
    compressiveStrengthUnit: values["compressiveStrength_unit"]
      ? Number(values["compressiveStrength_unit"])
      : undefined,
    tensileStrength: (values["tensileStrength"] as string) || "",
    tensileStrengthUnit: values["tensileStrength_unit"]
      ? Number(values["tensileStrength_unit"])
      : undefined,
    temperature: (values["temperature"] as string) || "",
    temperatureUnitId: values["temperature_unit"]
      ? Number(values["temperature_unit"])
      : undefined,
    diameter: (values["diameter"] as string) || "",
    diameterUnitId: values["diameterUnit"]
      ? Number(values["diameterUnit"])
      : undefined,
    coverage: (values["coverage"] as string) || "",
    coverageUnitId: values["coverageUnit"]
      ? Number(values["coverageUnit"])
      : undefined,
    thickness: (values["thickness"] as string) || "",
    thicknessUnitId: values["thicknessUnit"]
      ? Number(values["thicknessUnit"])
      : undefined,
    recycledContent: (values["recycledContent"] as string) || "",
    materialImageUrl: pickBackgroundFileUrl(values, "materialImageUrl", "materialImage"),
    publicationFileUrl: pickBackgroundFileUrl(values, "publicationFileUrl", "publicationFile"),
    dataType:
      normalizeDataTypeValue(values["dataType"], dataTypeOptions) ||
      (values["dataType"] as string) ||
      "",
    dataReview: (values["dataReview"] as string) || "",
    dataAcquisition: (values["dataAcquisition"] as string) || "",
    dataCompleteness: computeDataCompleteness(values) || (values["dataCompleteness"] as string) || "",
    ...geoFields,
    a1: (values["a1"] as string) === "true",
    a2: (values["a2"] as string) === "true",
    a3: (values["a3"] as string) === "true",
    a1_a3: (values["a1_a3"] as string) === "true",
    timelineAvailable: (values["timelineAvailable"] as string) || "",
    collectionStartYear: (values["collectionStartYear"] as string) || "",
    collectionStartMonth: (values["collectionStartMonth"] as string) || "",
    collectionStartDay: (values["collectionStartDay"] as string) || "",
    collectionEndYear: (values["collectionEndYear"] as string) || "",
    collectionEndMonth: (values["collectionEndMonth"] as string) || "",
    collectionEndDay: (values["collectionEndDay"] as string) || "",
    technologyId: values["technologyId"]
      ? Number(values["technologyId"])
      : undefined,
    manufacturerId: values["manufacturerId"]
      ? Number(values["manufacturerId"])
      : undefined,
    rawMaterials: rawMaterialsPayload,
    dataReviewers: dataReviewersPayload,
  };
}

